import { memo } from 'react';
import {
  Autocomplete,
  Box,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type {
  BankStatementImportCategoryOption,
  BankStatementImportCurrencyOption,
  BankStatementImportFinanceOption,
  BankStatementImportRelationOption,
} from '../types';
import type { ReviewedBankStatementTransaction, ReviewedStatementGroup } from '../reviewHelpers';
import { DraftTextField } from './DraftTextField';
import { TransactionRowEditor } from './TransactionRowEditor';
import {
  ellipsisTextFieldSx,
  formatSourcePeriod,
  nullableNumber,
} from './bankStatementImportUiUtils';
import { validateStatement } from '../validation';

type StatementGroupEditorProps = {
  group: ReviewedStatementGroup;
  failedTransactionRowId: string | null;
  transactionErrorMessage: string | null;
  invoiceOptions: BankStatementImportRelationOption[];
  onFileBusyChange: (rowId: string, busy: boolean) => void;
  categoryOptions: BankStatementImportCategoryOption[];
  counterpartyOptions: BankStatementImportRelationOption[];
  companyOptions: BankStatementImportRelationOption[];
  financeOptions: BankStatementImportFinanceOption[];
  currencyOptions: BankStatementImportCurrencyOption[];
  disabled: boolean;
  onStatementChange: (
    group: ReviewedStatementGroup,
    patch: Partial<ReviewedStatementGroup>
  ) => void;
  onTransactionChange: (rowId: string, patch: Partial<ReviewedBankStatementTransaction>) => void;
  onTransactionDelete: (rowId: string) => void;
  onDelete: (group: ReviewedStatementGroup) => void;
};

export const StatementGroupEditor = memo(
  ({
    group,
    failedTransactionRowId,
    transactionErrorMessage,
    invoiceOptions,
    onFileBusyChange,
    categoryOptions,
    counterpartyOptions,
    companyOptions,
    financeOptions,
    currencyOptions,
    disabled,
    onStatementChange,
    onTransactionChange,
    onTransactionDelete,
    onDelete,
  }: StatementGroupEditorProps) => {
    const errors = validateStatement(group);
    const selectedCompany =
      companyOptions.find((option) => option.Id === group.CrmRelationId) ??
      (group.CrmRelationId && group.CompanyName
        ? { Id: group.CrmRelationId, Name: group.CompanyName }
        : null);
    const availableFinanceOptions = financeOptions.filter(
      (option) => !group.CrmRelationId || option.RelationId === group.CrmRelationId
    );
    const selectedFinance =
      availableFinanceOptions.find((option) => option.Id === group.CrmRelationFinanceId) ??
      (group.CrmRelationFinanceId && group.BankAccountName
        ? {
            Id: group.CrmRelationFinanceId,
            RelationId: group.CrmRelationId ?? 0,
            CurrencyId: group.CrmCurrencyId ?? 0,
            Name: group.BankAccountName,
            BankName: group.BankName,
          }
        : null);
    const selectedCurrency =
      currencyOptions.find((option) => option.Id === group.CrmCurrencyId) ??
      (group.CrmCurrencyId && group.AccountCurrency
        ? {
            Id: group.CrmCurrencyId,
            Code: group.AccountCurrency,
            DisplayName: group.AccountCurrency,
          }
        : null);

    return (
      <Paper variant="outlined" sx={{ p: { xs: 1.25, md: 2 }, borderRadius: 2 }}>
        <Stack spacing={1.5}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ md: 'center' }}
          >
            <Typography variant="subtitle1" sx={{ flex: 1 }}>
              Statement {formatSourcePeriod(group.fromDate, group.toDate)} (
              {group.transactions.length})
            </Typography>
            <TextField
              required
              label="Statement Date"
              type="date"
              size="small"
              value={group.statementDate}
              onChange={(event) => onStatementChange(group, { statementDate: event.target.value })}
              disabled={disabled}
              InputLabelProps={{ shrink: true }}
              error={!!errors.statementDate}
              helperText={errors.statementDate || ' '}
              sx={{ width: 180 }}
            />
            <Tooltip title="Remove statement">
              <span>
                <IconButton
                  aria-label="Remove statement"
                  color="error"
                  onClick={() => onDelete(group)}
                  disabled={disabled}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
              gap: 1.25,
            }}
          >
            <Autocomplete
              size="small"
              options={companyOptions}
              value={selectedCompany}
              getOptionLabel={(option) => option.Name}
              isOptionEqualToValue={(option, value) => option.Id === value.Id}
              onChange={(_, value) =>
                onStatementChange(group, {
                  CrmRelationId: value?.Id ?? null,
                  CompanyName: value?.Name ?? null,
                  CrmRelationFinanceId: null,
                  BankAccountName: null,
                  BankName: null,
                  CrmCurrencyId: null,
                  AccountCurrency: null,
                })
              }
              disabled={disabled}
              renderInput={(params) => (
                <Tooltip title={selectedCompany?.Name || 'Unresolved'} placement="top-start">
                  <TextField
                    {...params}
                    label="Company"
                    required
                    error={!!errors.company}
                    helperText={errors.company || ' '}
                    sx={ellipsisTextFieldSx}
                  />
                </Tooltip>
              )}
            />
            <Autocomplete
              size="small"
              options={availableFinanceOptions}
              value={selectedFinance}
              getOptionLabel={(option) => option.Name}
              isOptionEqualToValue={(option, value) => option.Id === value.Id}
              onChange={(_, value) =>
                onStatementChange(group, {
                  CrmRelationId: value?.RelationId ?? null,
                  CrmRelationFinanceId: value?.Id ?? null,
                  BankAccountName: value?.Name ?? null,
                  BankName: value?.BankName ?? null,
                  CrmCurrencyId: value?.CurrencyId ?? null,
                  AccountCurrency:
                    currencyOptions.find((currency) => currency.Id === value?.CurrencyId)?.Code ??
                    null,
                  CompanyName:
                    companyOptions.find((company) => company.Id === value?.RelationId)?.Name ??
                    group.CompanyName,
                })
              }
              disabled={disabled || !group.CrmRelationId}
              renderInput={(params) => (
                <Tooltip title={selectedFinance?.Name || 'Unresolved'} placement="top-start">
                  <TextField
                    {...params}
                    label="Bank Account"
                    required
                    error={!!errors.bankAccount}
                    helperText={
                      !group.CrmRelationId ? 'Select a company first' : errors.bankAccount || ' '
                    }
                    sx={ellipsisTextFieldSx}
                  />
                </Tooltip>
              )}
            />
            <Autocomplete
              size="small"
              options={currencyOptions.filter(
                (currency) => !selectedFinance || currency.Id === selectedFinance.CurrencyId
              )}
              value={selectedCurrency}
              getOptionLabel={(option) => option.DisplayName || option.Code}
              isOptionEqualToValue={(option, value) => option.Id === value.Id}
              onChange={(_, value) =>
                onStatementChange(group, {
                  CrmCurrencyId: value?.Id ?? null,
                  AccountCurrency: value?.Code ?? null,
                })
              }
              disabled={disabled}
              renderInput={(params) => (
                <Tooltip
                  title={selectedCurrency?.DisplayName || selectedCurrency?.Code || 'Unresolved'}
                >
                  <TextField
                    {...params}
                    label="Currency"
                    required
                    error={!!errors.currency}
                    helperText={errors.currency || ' '}
                    sx={ellipsisTextFieldSx}
                  />
                </Tooltip>
              )}
            />
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 1.25,
            }}
          >
            <TextField
              required
              label="From Date"
              type="date"
              size="small"
              value={group.fromDate}
              onChange={(event) => onStatementChange(group, { fromDate: event.target.value })}
              disabled={disabled}
              InputLabelProps={{ shrink: true }}
              error={!!errors.fromDate}
              helperText={errors.fromDate || ' '}
            />
            <TextField
              required
              label="To Date"
              type="date"
              size="small"
              value={group.toDate}
              onChange={(event) => onStatementChange(group, { toDate: event.target.value })}
              disabled={disabled}
              InputLabelProps={{ shrink: true }}
              error={!!errors.toDate}
              helperText={errors.toDate || ' '}
            />
            <DraftTextField
              required
              label="Opening Balance"
              type="number"
              initialValue={group.openingBalance == null ? '' : String(group.openingBalance)}
              disabled={disabled}
              error={!!errors.openingBalance}
              helperText={errors.openingBalance || ' '}
              onCommit={(value) =>
                onStatementChange(group, { openingBalance: nullableNumber(value) })
              }
            />
            <DraftTextField
              required
              label="Closing Balance"
              type="number"
              initialValue={group.closingBalance == null ? '' : String(group.closingBalance)}
              disabled={disabled}
              error={!!errors.closingBalance}
              helperText={errors.closingBalance || ' '}
              onCommit={(value) =>
                onStatementChange(group, { closingBalance: nullableNumber(value) })
              }
            />
          </Box>
          <Divider />
          <Stack spacing={1}>
            {group.transactions.map((row, index) => (
              <TransactionRowEditor
                key={row.RowId}
                row={row}
                validationError={
                  row.RowId === failedTransactionRowId ? transactionErrorMessage : null
                }
                index={index}
                invoiceOptions={invoiceOptions}
                onFileBusyChange={onFileBusyChange}
                categoryOptions={categoryOptions}
                counterpartyOptions={counterpartyOptions}
                currencyOptions={currencyOptions}
                disabled={disabled}
                onChange={onTransactionChange}
                onDelete={onTransactionDelete}
              />
            ))}
          </Stack>
        </Stack>
      </Paper>
    );
  }
);
